import type { Bounds, Vector } from '@ardoq/graph';
import {
  BlocksTooltip,
  BlocksViewCommands,
  BlocksViewGraph,
  BlocksViewLink,
  BlocksViewNode,
  BlocksViewVisualState,
  Controller,
  GetBlocksTooltipContent,
  InteractionMode,
  Interactions,
  LayoutType,
  LinksAggregation,
  UndoStack,
} from '../types';
import { BLOCKS_VIEW_FIT_PADDING } from '../consts';
import { rectCenter, rectHeight, rectWidth } from '../misc/geometry';
import { isGroup } from '../viewModel$/util';
import { applyLayout } from '../layout/layout';
import { getLayoutData } from '../layout/util';
import { withoutArrangementsFor } from '../layout/arrangementLayout';
import type { BlocksArrangement } from 'tabview/assistantBlocks/arrangementRecipe';
import { createProjection } from '../misc/projection';
import { POPOVER_DELAY } from '@ardoq/popovers';
import {
  editCanUndo,
  editFlipHorz,
  editFlipVert,
  editReset,
  editRotate,
  editUndo,
} from '../layout/edit';
import { renderGraph } from '../visual/visual';
import { colors } from '@ardoq/design-tokens';
import { type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { getLinked } from '../misc/graph';
import { createItemSet } from './itemSet';
import type { I18nLocale } from '@ardoq/i18n';

type PendingTooltip = {
  item: BlocksViewNode | BlocksViewLink;
  content: ReactNode;
  position: Vector;
};

type CreateControllerArgs = {
  canvas: HTMLCanvasElement;
  graph: BlocksViewGraph;
  viewInstanceId: string;
  appRefresh: () => void;
  linksAggregation: LinksAggregation;
  i18nLocale: I18nLocale;
  isPresentationMode: boolean;
  setTooltip: (tooltip: BlocksTooltip | null) => void;
  getTooltipContent: GetBlocksTooltipContent;
  onRenderComplete: VoidFunction;
  commands: BlocksViewCommands;
  getArrangements: () => BlocksArrangement[];
  getCoveredContainerIds: () => (string | null)[];
};

export const createController = ({
  canvas,
  graph,
  viewInstanceId,
  appRefresh,
  linksAggregation,
  i18nLocale,
  isPresentationMode,
  setTooltip,
  getTooltipContent,
  onRenderComplete,
  commands,
  getArrangements,
  getCoveredContainerIds,
}: CreateControllerArgs): Controller => {
  const projection = createProjection();

  projection.adjustToViewport(
    [
      canvas.offsetLeft,
      canvas.offsetTop,
      canvas.offsetLeft + canvas.offsetWidth,
      canvas.offsetTop + canvas.offsetHeight,
    ],
    window.devicePixelRatio
  );

  /* interaction mode */

  const setInteractionMode = (value: InteractionMode) => {
    if (value !== interactionMode) {
      interactionMode = value;
      appRefresh();
    }
  };
  let interactionMode = InteractionMode.None;

  /* interactions */

  const attach = (value: Interactions) => {
    if (interactions && interactions !== value) {
      interactions.detach();
    }

    interactions = value;
    interactions.attach();
  };

  const detach = () => {
    clearPendingTooltip();

    if (cancelTooltipTimeoutId) {
      clearTimeout(cancelTooltipTimeoutId);
      cancelTooltipTimeoutId = 0;
    }

    if (interactions) {
      interactions.detach();
      interactions = undefined;
    }
  };

  const viewActionsConfigs = (i18nLocale: I18nLocale) => {
    return interactions ? interactions.viewActionsConfigs(i18nLocale) : [];
  };

  let interactions: Interactions | undefined; // = undefined;

  /* actions */

  const notifyLayoutChange = () =>
    commands.onLayoutChange(getLayoutData(graph.root.children));

  const fit = (rc: Bounds, doAnimate: boolean) => {
    projection.adjustToRect(rc, BLOCKS_VIEW_FIT_PADDING, doAnimate);
    renderCanvas();
  };

  const zoomIn = () => {
    zoomTo(rectCenter(projection.window(false)), 1.1, false);
  };
  const zoomOut = () => {
    zoomTo(rectCenter(projection.window(false)), 0.9, false);
  };
  const zoomTo = (wd: Vector, value: number, doAnimate: boolean) => {
    projection.zoomToPoint(wd, projection.scale(true) * value, doAnimate);
    renderCanvas();
  };

  const panBy = (amount: Vector) => {
    projection.translateBy(amount, false);
    renderCanvas();
  };
  const expandAll = () => setOpenAll(true);
  const collapseAll = () => setOpenAll(false);
  const setOpenAll = (value: boolean) => {
    const doit = (node: BlocksViewNode) => {
      if (isGroup(node)) {
        if (node.parent) {
          node.open = value;
        }

        for (const child of node.children) {
          doit(child);
        }
      }
    };

    const wd = rectCenter(projection.window(false));
    const vp = rectCenter(projection.viewport());

    doit(graph.root);
    notifyLayoutChange();
    applyLayout(graph, linksAggregation);
    projection.adjustToPoint(wd, vp, true);
    renderCanvas();
  };

  const expand = (node: BlocksViewNode) => setOpen(node, true);
  const collapse = (node: BlocksViewNode) => setOpen(node, false);
  const setOpen = (node: BlocksViewNode, value: boolean) => {
    const vp = projection.toViewport([node.bounds[0], node.bounds[1]]);

    node.open = value;
    notifyLayoutChange();
    applyLayout(graph, linksAggregation);
    projection.adjustToPoint([node.bounds[0], node.bounds[1]], vp, true);
    renderCanvas();
  };

  const undoStack: UndoStack = [];

  const rotate = (groups: Iterable<BlocksViewNode>) => {
    editRotate(undoStack, groups);
    notifyLayoutChange();
    applyLayout(graph, linksAggregation);
    renderCanvas();
  };

  const flipHorizontal = (groups: Iterable<BlocksViewNode>) => {
    editFlipHorz(undoStack, groups);
    notifyLayoutChange();
    applyLayout(graph, linksAggregation);
    renderCanvas();
  };

  const flipVertical = (groups: Iterable<BlocksViewNode>) => {
    editFlipVert(undoStack, groups);
    notifyLayoutChange();
    applyLayout(graph, linksAggregation);
    renderCanvas();
  };

  const resetLayout = (
    groups: Iterable<BlocksViewNode>,
    layoutType: LayoutType
  ) => {
    const resetGroups = [...groups];
    editReset(undoStack, resetGroups, layoutType);
    const arrangements = getArrangements();
    const remaining = withoutArrangementsFor(
      arrangements,
      getCoveredContainerIds(),
      resetGroups
    );
    if (remaining === arrangements) notifyLayoutChange();
    else commands.onLayoutReset(getLayoutData(graph.root.children), remaining);
    applyLayout(graph, linksAggregation);
    renderCanvas();
  };

  const undo = () => {
    if (editCanUndo(undoStack)) {
      editUndo(undoStack);
      notifyLayoutChange();
      applyLayout(graph, linksAggregation);
      renderCanvas();
    }
  };

  const paintCanvas = () => {
    animationFrameId = 0;

    // ideally, I'd do this
    projection.adjustToViewport(
      [
        canvas.offsetLeft,
        canvas.offsetTop,
        canvas.offsetLeft + canvas.offsetWidth,
        canvas.offsetTop + canvas.offsetHeight,
      ],
      window.devicePixelRatio
    );

    const viewport = projection.viewport();
    canvas.width = rectWidth(viewport);
    canvas.height = rectHeight(viewport);

    // using an opaque rendering context is faster, but means that we
    // must clear the invalid region before each render

    const ctx = canvas.getContext('2d', { alpha: false })!;
    ctx.resetTransform();
    ctx.fillStyle = colors.white950; // document.activeElement === canvas ? 'white' : '#f8f8f8';
    ctx.fillRect(
      viewport[0],
      viewport[1],
      rectWidth(viewport),
      rectHeight(viewport)
    );

    const notFinishedRendering = interactions && !interactions.renderCanvas();

    if (notFinishedRendering || projection.isAnimating()) {
      renderCanvas();
    } else {
      onRenderComplete();
    }
  };

  const renderCanvas = () => {
    if (interactions && animationFrameId === 0) {
      animationFrameId = window.requestAnimationFrame(paintCanvas);
    }
  };

  const redrawImmediate = () => {
    if (!interactions) {
      return;
    }
    window.cancelAnimationFrame(animationFrameId);
    paintCanvas();
  };

  const createExportCanvas = () => {
    const { width, height } = canvas;
    const result = new OffscreenCanvas(width, height);
    const exportContext = result.getContext('2d', { alpha: false });
    if (!exportContext) {
      return null;
    }
    const [left, top] = projection.viewport();
    exportContext.fillStyle = colors.white950;
    exportContext.fillRect(left, top, width, height);
    exportContext.setTransform(projection.matrix());
    renderGraph(graph, projection.window(false), 'export', exportContext);
    return result;
  };

  let animationFrameId = 0;
  let isTooltipShown = false;
  /**
   * Timer ID for scheduled tooltip cancellation.
   * Used to implement a grace period when cursor leaves canvas, allowing time to move to popover.
   */
  let cancelTooltipTimeoutId = 0;
  /** Timer ID for the pending open of a tooltip that has been hovered but not yet shown. */
  let showTooltipTimeoutId = 0;

  let pendingTooltip: PendingTooltip | null = null;

  const hideTooltip = () => {
    isTooltipShown = false;
    setTooltip(null);
  };

  const showTooltip = (reactNode: ReactNode, position: Vector) => {
    if (cancelTooltipTimeoutId) {
      clearTimeout(cancelTooltipTimeoutId);
      cancelTooltipTimeoutId = 0;
    }

    isTooltipShown = true;
    setTooltip({
      point: { x: position[0], y: position[1] },
      content: reactNode,
    });
  };

  const clearPendingTooltip = () => {
    if (showTooltipTimeoutId) {
      clearTimeout(showTooltipTimeoutId);
      showTooltipTimeoutId = 0;
    }
    pendingTooltip = null;
  };

  /**
   * Arms the open timer the first time an item is hovered and leaves it running
   * while the pointer stays on that same item, so the popover opens after
   * POPOVER_DELAY of hovering rather than after POPOVER_DELAY of the pointer
   * being motionless. Only the anchor position keeps following the pointer.
   */
  const scheduleTooltipShow = (
    item: BlocksViewNode | BlocksViewLink,
    content: ReactNode,
    position: Vector
  ) => {
    if (pendingTooltip && pendingTooltip.item === item) {
      pendingTooltip = { item, content, position };
      return;
    }

    clearPendingTooltip();
    pendingTooltip = { item, content, position };
    showTooltipTimeoutId = window.setTimeout(() => {
      showTooltipTimeoutId = 0;
      const tooltip = pendingTooltip;
      pendingTooltip = null;

      if (tooltip) {
        showTooltip(tooltip.content, tooltip.position);
      }
    }, POPOVER_DELAY);
  };

  /**
   * Critical: Cancel scheduled closing when cursor reaches the popover.
   * Without this, cursor movements would trigger timeout before reaching popover.
   */
  const onTooltipMouseEnter = () => {
    if (cancelTooltipTimeoutId) {
      clearTimeout(cancelTooltipTimeoutId);
      cancelTooltipTimeoutId = 0;
    }
  };

  const onTooltipMouseLeave = (event: ReactMouseEvent) => {
    const relatedTarget = event.relatedTarget;
    if (
      relatedTarget instanceof Element &&
      (relatedTarget === canvas || canvas.contains(relatedTarget))
    ) {
      return;
    }
    hideTooltip();
  };

  const cancelTooltip = () => {
    if (cancelTooltipTimeoutId) {
      clearTimeout(cancelTooltipTimeoutId);
      cancelTooltipTimeoutId = 0;
    }
    clearPendingTooltip();
    hideTooltip();
  };

  /**
   * Schedules tooltip cancellation with a grace period.
   *
   * This solves two conflicting requirements:
   * 1. Close tooltip when cursor leaves canvas to panels/other areas (ARD-31194)
   * 2. Keep tooltip open when cursor moves from canvas to popover to click URLs (ARD-28305)
   *
   * Solution: Use POPOVER_DELAY (200ms) grace period between canvas exit and closing.
   * - If cursor reaches popover within 200ms → onTooltipMouseEnter cancels the timer
   * - If cursor doesn't return → tooltip closes after 200ms
   *
   * Note: This grace period is needed because there's a physical gap between canvas
   * and popover DOM elements.
   */
  const scheduleTooltipCancel = () => {
    // Cancel pending (not yet shown) tooltip immediately
    clearPendingTooltip();

    // Schedule cancellation of shown tooltip after grace period
    if (cancelTooltipTimeoutId) {
      clearTimeout(cancelTooltipTimeoutId);
    }
    cancelTooltipTimeoutId = window.setTimeout(() => {
      cancelTooltipTimeoutId = 0;
      hideTooltip();
    }, POPOVER_DELAY);
  };

  const showNodeTooltip = (node: BlocksViewNode, wd: Vector) => {
    /**
     * Cancel any scheduled tooltip closing when pointer returns to an item.
     * Handles the case where cursor briefly leaves and quickly re-enters canvas:
     * 1. Cursor leaves canvas → scheduleTooltipCancel() starts 200ms timer
     * 2. Cursor returns to item within 200ms → this cancels the timer
     * 3. Tooltip stays open (correct behavior)
     */
    if (cancelTooltipTimeoutId) {
      clearTimeout(cancelTooltipTimeoutId);
      cancelTooltipTimeoutId = 0;
    }

    if (isTooltipShown) {
      return;
    }

    const content = getTooltipContent(node, {
      canvas,
      linksAggregation,
      i18nLocale,
    });
    if (!content) {
      return;
    }

    const { left, top } = canvas.getBoundingClientRect();
    const vp = projection.toViewport(wd);

    scheduleTooltipShow(node, content, [vp[0] + left, vp[1] + top]);
  };

  const showLinkTooltip = (link: BlocksViewLink, wd: Vector) => {
    // Cancel any scheduled tooltip closing when pointer returns to an item (see showNodeTooltip)
    if (cancelTooltipTimeoutId) {
      clearTimeout(cancelTooltipTimeoutId);
      cancelTooltipTimeoutId = 0;
    }

    if (isTooltipShown) {
      return;
    }

    const content = getTooltipContent(link, {
      canvas,
      linksAggregation,
      i18nLocale,
    });
    if (!content) {
      return;
    }

    const { left, top } = canvas.getBoundingClientRect();
    const vp = projection.toViewport(wd);

    scheduleTooltipShow(link, content, [vp[0] + left, vp[1] + top - 4]);
  };

  const highlightLinked = (node: BlocksViewNode | null) => {
    highlight.clear();

    if (node) {
      highlight = getLinked(node);

      if (highlight.size() < 3) {
        highlight.clear();
      }
    }
  };
  let highlight = createItemSet(BlocksViewVisualState.Highlight);

  return {
    setInteractionMode,
    getInteractionMode: () => interactionMode,
    viewActionsConfigs,

    attach,
    detach,

    fit,
    zoomTo,
    zoomIn,
    zoomOut,
    panBy,

    expandAll,
    collapseAll,
    setOpenAll,

    expand,
    collapse,
    setOpen,

    rotate,
    flipHorizontal,
    flipVertical,
    resetLayout,

    undo,

    highlightLinked: highlightLinked,
    getHighlight: () => highlight,

    renderCanvas,
    redrawImmediate,
    createExportCanvas,

    commands,
    notifyLayoutChange,

    showNodeTooltip,
    showLinkTooltip,
    cancelTooltip,
    scheduleTooltipCancel,
    onTooltipMouseEnter,
    onTooltipMouseLeave,

    selection: createItemSet(BlocksViewVisualState.Select),

    canvas,
    projection,
    graph,
    viewInstanceId,
    linksAggregation,
    isPresentationMode,

    pointerId: NaN,
    pointerDevice: [NaN, NaN],
    pointerWorld: [NaN, NaN],
    pointerDownDevice: [NaN, NaN],
    pointerDownWorld: [NaN, NaN],
    hover: createItemSet(BlocksViewVisualState.Hover),

    editPreview: null,
    undoStack,
  };
};
